/**
 * Paints {@link Shape}s with Canvas 2D (the processor). Used for the whole picture when there is
 * no graphics card to use, and for the top layer (names, counts, handles) in every case.
 */
import type { ImageLookup, ImageShape, Shape } from "./shapes";

/** Clears the canvas and sets it up to draw in CSS pixels. */
export function clear2d(ctx: CanvasRenderingContext2D, width: number, height: number, dpr: number): void {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, width, height);
}

/** Paints `shapes`, in order, over what the canvas holds; pictures come from `images`. Gives how many pictures it painted. */
export function paint2d(ctx: CanvasRenderingContext2D, shapes: readonly Shape[], images?: ImageLookup): number {
  let pictures = 0;
  for (const s of shapes) {
    if (s.type === "text") {
      text(ctx, s);
      continue;
    }
    if (s.type === "image") {
      const source = images?.(s.key);
      if (source) {
        image(ctx, s, source);
        pictures++;
      }
      continue;
    }
    ctx.beginPath();
    switch (s.type) {
      case "circle":
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        break;
      case "box":
        if (s.angle) {
          // Turned: its four corners (round corners are only used on boxes that are not turned).
          const c = Math.cos(s.angle);
          const n = Math.sin(s.angle);
          for (const [dx, dy] of [[-s.hw, -s.hh], [s.hw, -s.hh], [s.hw, s.hh], [-s.hw, s.hh]] as const) ctx.lineTo(s.x + dx * c - dy * n, s.y + dx * n + dy * c);
          ctx.closePath();
        } else {
          ctx.roundRect(s.x - s.hw, s.y - s.hh, s.hw * 2, s.hh * 2, s.radius);
        }
        break;
      case "hexagon":
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          ctx.lineTo(s.x + Math.cos(angle) * s.r, s.y + Math.sin(angle) * s.r);
        }
        ctx.closePath();
        break;
      case "triangle": {
        const bx = s.x - s.ux * s.length;
        const by = s.y - s.uy * s.length;
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(bx - s.uy * s.spread, by + s.ux * s.spread);
        ctx.lineTo(bx + s.uy * s.spread, by - s.ux * s.spread);
        ctx.closePath();
        break;
      }
      case "segment":
        ctx.moveTo(s.x1, s.y1);
        ctx.lineTo(s.x2, s.y2);
        break;
      case "chevron": {
        const bx = s.x - s.ux * s.length;
        const by = s.y - s.uy * s.length;
        ctx.moveTo(bx - s.uy * s.spread, by + s.ux * s.spread);
        ctx.lineTo(s.x, s.y);
        ctx.lineTo(bx + s.uy * s.spread, by - s.ux * s.spread);
        break;
      }
    }
    const { fill, stroke, width, alpha, dash } = s.paint;
    ctx.globalAlpha = alpha;
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.lineCap = s.type === "segment" && s.round ? "round" : "butt";
      ctx.setLineDash(dash ?? []);
      ctx.stroke();
      if (dash) ctx.setLineDash([]);
    }
  }
  ctx.globalAlpha = 1;
  ctx.lineCap = "butt";
  return pictures;
}

/** A picture cut to its disc, filling it (cropped to the middle, never stretched). */
function image(ctx: CanvasRenderingContext2D, s: ImageShape, source: CanvasImageSource): void {
  const [w, h] = sizeOf(source);
  if (!w || !h || s.r <= 0) return;
  const scale = (s.r * 2) / Math.min(w, h);
  ctx.save();
  ctx.globalAlpha = s.alpha;
  ctx.beginPath();
  ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(source, s.x - (w * scale) / 2, s.y - (h * scale) / 2, w * scale, h * scale);
  ctx.restore();
}

/** A picture's own size in pixels (an image's natural size). */
function sizeOf(source: CanvasImageSource): [number, number] {
  if (source instanceof HTMLImageElement) return [source.naturalWidth, source.naturalHeight];
  if ("width" in source && typeof source.width === "number") return [source.width, source.height as number];
  return [0, 0];
}

/** One line of text, with its halo first. */
function text(ctx: CanvasRenderingContext2D, s: Extract<Shape, { type: "text" }>): void {
  ctx.font = s.font;
  ctx.textAlign = s.align;
  ctx.textBaseline = s.baseline;
  ctx.globalAlpha = s.alpha;
  if (s.halo) {
    ctx.lineJoin = "round";
    ctx.lineWidth = s.halo.width;
    ctx.strokeStyle = s.halo.color;
    ctx.strokeText(s.text, s.x, s.y);
  }
  ctx.fillStyle = s.color;
  ctx.fillText(s.text, s.x, s.y);
}
